import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaChevronLeft, FaBookmark, FaBookmark as FaBookmarkSolid } from 'react-icons/fa';
import { jobPostingService, JobPosting } from '../services/jobPostingService';
import LoadingSpinner from '../components/LoadingSpinner';
import CareersHeader from '../components/CareersHeader';
import { useMobile } from '../hooks/useMobile';
import { apiClient } from '../services/apiClient';

const JobDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const [jobPosting, setJobPosting] = useState<JobPosting | null>(null);
  const [jobDescriptionContent, setJobDescriptionContent] = useState<string>('');
  const [loadingDescriptionContent, setLoadingDescriptionContent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savedJobs, setSavedJobs] = useState<Set<string>>(new Set());


  useEffect(() => {
    loadJobPosting();
    // Load saved jobs from localStorage
    const saved = localStorage.getItem('savedJobs');
    if (saved) {
      setSavedJobs(new Set(JSON.parse(saved)));
    }
  }, [id]);

  const loadJobPosting = async () => {
    if (!id) {
      navigate('/careers');
      return;
    }

    try {
      setLoading(true);
      const posting = await jobPostingService.getJobPosting(id);
      setJobPosting(posting);
      
      // Fetch job description content if jobDescriptionId exists
      if (posting.jobDescriptionId) {
        try {
          setLoadingDescriptionContent(true);
          const contentResponse = await apiClient.get(`/job-descriptions/${posting.jobDescriptionId}/content`);
          setJobDescriptionContent(contentResponse.data?.content || 'Job description content not available.');
        } catch (error) {
          console.error('Error loading job description content:', error);
          setJobDescriptionContent('Error loading job description content.');
        } finally {
          setLoadingDescriptionContent(false);
        }
      } else {
        setJobDescriptionContent('No job description assigned.');
      }
    } catch (error: any) {
      console.error('Error loading job posting:', error);
      navigate('/careers');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveJob = () => {
    if (!jobPosting) return;
    const newSavedJobs = new Set(savedJobs);
    if (newSavedJobs.has(jobPosting.id)) {
      newSavedJobs.delete(jobPosting.id);
    } else {
      newSavedJobs.add(jobPosting.id);
    }
    setSavedJobs(newSavedJobs);
    localStorage.setItem('savedJobs', JSON.stringify(Array.from(newSavedJobs)));
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new Event('savedJobsUpdated'));
  };

  const handleApply = () => {
    if (jobPosting) {
      navigate(`/job/${jobPosting.id}/apply`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!jobPosting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Job Posting Not Found</h2>
          <Link to="/careers" className="text-blue-600 hover:underline">
            Back to Careers
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CareersHeader />

      {/* Content with top padding to account for fixed header */}
      <div className="pt-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back button */}
          <button
            onClick={() => navigate('/careers')}
            className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 mb-6 transition-colors"
          >
            <FaChevronLeft className="w-4 h-4" />
            <span>Back to Job Search</span>
          </button>

          {/* Job Details Card */}
          <div className="bg-white rounded-lg shadow-md p-8">
          {/* Job Title */}
          <h1 className="text-xl font-bold text-gray-900 mb-2">{jobPosting.jobTitle}</h1>

          {/* Job ID and Job Type - Same line */}
          <p className="text-xs text-gray-600 mb-3">
            {jobPosting.jobId && <span>Job ID: {jobPosting.jobId}</span>}
            {jobPosting.jobId && <span className="mx-2">|</span>}
            <span>{jobPosting.jobType}</span>
          </p>

        
            <div className="mb-6">
              <button
                onClick={handleApply}
                className={`bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors font-semibold text-sm ${isMobile ? 'w-full' : ''}`}
              >
                Apply for the Job
              </button>
            </div>

            {/* Separator Line */}
            <div className="border-t border-gray-300 mb-6"></div>

            {/* Job Description */}
            <div className="mb-8">
              <h2 className="text-base font-bold text-gray-900 mb-4">Job Description</h2>
              {loadingDescriptionContent ? (
                <div className="flex items-center space-x-2">
                  <LoadingSpinner size="sm" />
                  <span className="text-sm text-gray-600">Loading job description...</span>
                </div>
              ) : (
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {jobDescriptionContent}
                  </p>
                </div>
              )}
            </div>

            {/* Save Job Button */}
            <div className="pt-6 border-t border-gray-200">
              <button
                onClick={handleSaveJob}
                className={`flex items-center space-x-2 px-6 py-2 rounded-md transition-colors text-sm ${
                  savedJobs.has(jobPosting.id)
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {savedJobs.has(jobPosting.id) ? (
                  <>
                    <FaBookmarkSolid className="w-4 h-4" />
                    <span>Saved</span>
                  </>
                ) : (
                  <>
                    <FaBookmark className="w-4 h-4" />
                    <span>Save</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default JobDetails;

